import { useState, useCallback } from 'react'
import { BookingSlot, Booking, BrandRequest } from '../data/types'

interface UseBookingsReturn {
  selectedSlot: BookingSlot | null
  isPanelOpen: boolean
  bookings: Booking[]
  brandRequests: BrandRequest[]
  selectSlot: (slot: BookingSlot, isAdmin?: boolean) => void
  closePanel: () => void
  confirmBooking: (notes?: string) => void
  blockSlot: (slot: BookingSlot) => void
  unblockSlot: (slot: BookingSlot) => void
  overrideSlot: (slot: BookingSlot) => void
  removeAvailability: (slot: BookingSlot) => void
  bookInternal: (
    slot: BookingSlot,
    notes?: string,
    campaignName?: string,
  ) => void
  unblockToAvailable: (slot: BookingSlot) => void
  sendBrandRequest: (
    request: Omit<BrandRequest, 'id' | 'status' | 'createdAt' | 'requestedBy'>,
    mode?: 'planned' | 'pending',
  ) => void
  updateBrandRequest: (
    requestId: string,
    request: Omit<BrandRequest, 'id' | 'status' | 'createdAt' | 'requestedBy'>,
    sendNow?: boolean,
  ) => void
  activateRequest: (requestId: string) => void
  approveRequest: (
    requestId: string,
    approverName: string,
    approverEmail: string,
    conditions?: string,
  ) => void
  rejectRequest: (requestId: string, approverName: string) => void
  removeRequest: (requestId: string) => void
  respondToInvitation: (
    requestId: string,
    response: 'accepted' | 'rejected',
    message?: string,
  ) => void
}

export function useBookings(): UseBookingsReturn {
  const [selectedSlot, setSelectedSlot] = useState<BookingSlot | null>(null)
  const [isPanelOpen, setIsPanelOpen] = useState(false)
  const [bookings, setBookings] = useState<Booking[]>([])
  const [brandRequests, setBrandRequests] = useState<BrandRequest[]>([
    {
      id: 'req-1',
      brandId: 'brand-1',
      brandName: 'Chanel',
      stores: [{ storeId: 'store-1', storeName: 'V&A Waterfront' }],
      mediaType: 'Digital Outposts',
      mediaTypes: ['Digital Outposts'],
      weekNumber: 2,
      weekStartDate: '2026-03-10',
      weekEndDate: '2026-03-16',
      status: 'pending',
      requestedBy: 'woolworths',
      createdAt: new Date().toISOString(),
      campaignName: 'Summer Launch',
      contactName: 'Jane Doe',
      contactEmail: 'jane@prestige.co.za',
      contactJobTitle: 'Marketing Director',
    },
    {
      id: 'req-2',
      brandId: 'brand-3',
      brandName: 'Lancôme',
      stores: [{ storeId: 'store-3', storeName: 'Canal Walk' }],
      mediaType: 'Digital Drive Aisle',
      mediaTypes: ['Digital Drive Aisle', 'Digital Windows'],
      weekNumber: 4,
      weekStartDate: '2026-03-24',
      weekEndDate: '2026-03-30',
      status: 'pending',
      requestedBy: 'brand',
      createdAt: new Date('2026-03-12').toISOString(),
      campaignName: 'Autumn Skincare',
      contactName: 'Michelle du Plessis',
      contactEmail: 'michelle@elizabetharden.co.za',
      contactJobTitle: 'Marketing Manager',
    },
    {
      id: 'req-3',
      brandId: 'brand-6',
      brandName: 'Clarins',
      stores: [
        { storeId: 'store-1', storeName: 'V&A Waterfront' },
        { storeId: 'store-2', storeName: 'Sandton City' },
      ],
      mediaType: 'Digital Outposts',
      mediaTypes: ['Digital Outposts', 'Digital Drive Aisle'],
      weekNumber: 3,
      weekStartDate: '2026-03-17',
      weekEndDate: '2026-03-23',
      status: 'draft',
      requestedBy: 'woolworths',
      createdAt: new Date().toISOString(),
      campaignName: 'Winter Skincare',
      contactName: 'Priya Naidoo',
      contactEmail: 'priya@esteelauder.co.za',
      contactJobTitle: 'Regional Media Manager',
    },
  ])

  const selectSlot = useCallback((slot: BookingSlot, isAdmin?: boolean) => {
    if (slot.status === 'available' || (isAdmin && slot.status === 'blocked')) {
      setSelectedSlot(slot)
      setIsPanelOpen(true)
    }
  }, [])

  const closePanel = useCallback(() => {
    setIsPanelOpen(false)
    setTimeout(() => setSelectedSlot(null), 300)
  }, [])

  const confirmBooking = useCallback(
    (notes?: string) => {
      if (!selectedSlot) return

      const newBooking: Booking = {
        id: `booking-${Date.now()}`,
        storeId: selectedSlot.storeId,
        storeName: '',
        mediaType: selectedSlot.mediaType,
        weekNumber: selectedSlot.weekNumber,
        weekStartDate: selectedSlot.weekStartDate,
        weekEndDate: selectedSlot.weekEndDate,
        brandName: 'Woolworths Booking',
        brandId: 'brand-1',
        status: 'pending',
        price: selectedSlot.price,
        notes,
        createdAt: new Date().toISOString(),
      }

      setBookings((prev) => [...prev, newBooking])
      closePanel()
    },
    [selectedSlot, closePanel],
  )

  const blockSlot = useCallback((slot: BookingSlot) => {
    console.log('Blocking slot:', slot.id)
  }, [])

  const unblockSlot = useCallback((slot: BookingSlot) => {
    console.log('Unblocking slot:', slot.id)
  }, [])

  const overrideSlot = useCallback((slot: BookingSlot) => {
    console.log('Overriding slot:', slot.id)
  }, [])

  const removeAvailability = useCallback(
    (slot: BookingSlot) => {
      console.log('Removing availability for slot:', slot.id)
      // In a real app, this would update the slot status to 'removed'
      closePanel()
    },
    [closePanel],
  )

  const bookInternal = useCallback(
    (slot: BookingSlot, notes?: string, campaignName?: string) => {
      console.log(
        'Booking internal for slot:',
        slot.id,
        notes,
        'Campaign:',
        campaignName,
      )
      // In a real app, this would update the slot status to 'internal' with the campaign name
      closePanel()
    },
    [closePanel],
  )

  const unblockToAvailable = useCallback(
    (slot: BookingSlot) => {
      console.log('Unblocking slot to available:', slot.id)
      // In a real app, this would update the slot status to 'available'
      closePanel()
    },
    [closePanel],
  )

  const sendBrandRequest = useCallback(
    (
      requestData: Omit<BrandRequest, 'id' | 'status' | 'createdAt'> & {
        requestedBy?: 'woolworths' | 'brand'
      },
      sendNow: boolean = false,
    ) => {
      const newRequest: BrandRequest = {
        ...requestData,
        id: `req-${Date.now()}`,
        status: sendNow ? 'pending' : 'draft',
        requestedBy: requestData.requestedBy || 'woolworths',
        createdAt: new Date().toISOString(),
      }
      setBrandRequests((prev) => [newRequest, ...prev])
      console.log(
        sendNow ? 'Sent brand request:' : 'Created draft brand request:',
        newRequest,
      )
    },
    [],
  )

  const activateRequest = useCallback((requestId: string) => {
    setBrandRequests((prev) =>
      prev.map((r) =>
        r.id === requestId && r.status === 'draft'
          ? {
              ...r,
              status: 'pending' as const,
              createdAt: new Date().toISOString(),
            }
          : r,
      ),
    )
  }, [])

  const updateBrandRequest = useCallback(
    (
      requestId: string,
      requestData: Omit<
        BrandRequest,
        'id' | 'status' | 'createdAt' | 'requestedBy'
      >,
      sendNow: boolean = false,
    ) => {
      setBrandRequests((prev) =>
        prev.map((r) =>
          r.id === requestId
            ? {
                ...r,
                ...requestData,
                status: sendNow ? ('pending' as const) : r.status,
                createdAt: sendNow ? new Date().toISOString() : r.createdAt,
              }
            : r,
        ),
      )
      console.log(
        sendNow ? 'Updated & sent request:' : 'Updated draft:',
        requestId,
      )
    },
    [],
  )

  const removeRequest = useCallback((requestId: string) => {
    setBrandRequests((prev) => prev.filter((r) => r.id !== requestId))
  }, [])

  const approveRequest = useCallback(
    (
      requestId: string,
      approverName: string,
      approverEmail: string,
      conditions?: string,
    ) => {
      setBrandRequests((prev) =>
        prev.map((r) =>
          r.id === requestId
            ? {
                ...r,
                status: conditions
                  ? ('accepted-with-conditions' as const)
                  : ('accepted' as const),
                respondedAt: new Date().toISOString(),
                approvedBy: approverName,
                approverEmail,
                approvalConditions: conditions || undefined,
              }
            : r,
        ),
      )
    },
    [],
  )

  const rejectRequest = useCallback(
    (requestId: string, approverName: string) => {
      setBrandRequests((prev) =>
        prev.map((r) =>
          r.id === requestId
            ? {
                ...r,
                status: 'rejected' as const,
                respondedAt: new Date().toISOString(),
                approvedBy: approverName,
              }
            : r,
        ),
      )
    },
    [],
  )

  const respondToInvitation = useCallback(
    (
      requestId: string,
      response: 'accepted' | 'rejected',
      message?: string,
    ) => {
      setBrandRequests((prev) =>
        prev.map((r) => {
          if (r.id !== requestId) return r
          // If brand accepts a Woolworths-initiated invitation, it goes to 'brand-accepted'
          // (awaiting Woolworths final approval) instead of directly to 'accepted'
          const newStatus =
            response === 'accepted' && r.requestedBy === 'woolworths'
              ? ('brand-accepted' as const)
              : response
          return {
            ...r,
            status: newStatus,
            respondedAt: new Date().toISOString(),
            message: message || r.message,
          }
        }),
      )
    },
    [],
  )

  return {
    selectedSlot,
    isPanelOpen,
    bookings,
    brandRequests,
    selectSlot,
    closePanel,
    confirmBooking,
    blockSlot,
    unblockSlot,
    overrideSlot,
    removeAvailability,
    bookInternal,
    unblockToAvailable,
    sendBrandRequest,
    updateBrandRequest,
    activateRequest,
    approveRequest,
    rejectRequest,
    removeRequest,
    respondToInvitation,
  }
}
